import { HttpService } from '@/services';
import type {
  ChapterItemType,
  ChapterNote,
  ChapterType,
  Course,
  Exercise,
  FlashcardAndStats,
  FlashcardDecksAndStats,
  TestResult,
  Unit,
  UserCourseStatus,
} from '@/types';

export default class CourseService {
  public static async getCourses(): Promise<Course[]> {
    const response = await HttpService.get('/courses?limit=100');
    return response.data.data as Course[];
  }

  public static async getCourse(courseId: string): Promise<Course> {
    const response = await HttpService.get(`/courses/${courseId}`);
    return response.data.data as Course;
  }

  public static async getUserExercise(courseId: string, exerciseId: string): Promise<Exercise> {
    const response = await HttpService.get(`/users/me/exercises/${courseId}/${exerciseId}`);
    return response.data.data as Exercise;
  }

  public static async getNewUserExercise(courseId: string, exerciseId: string): Promise<Exercise> {
    const response = await HttpService.post(`/users/me/exercises/${courseId}/${exerciseId}`);
    return response.data.data as Exercise;
  }

  public static async patchUserExercise(courseId: string, exercise: Exercise): Promise<Exercise> {
    const { questions, id, status } = exercise;
    const response = await HttpService.patch(`/users/me/exercises/${courseId}/${id}`, {
      questions,
      status,
    });
    return response.data.data as Exercise;
  }

  public static async getUnit(courseId: string, unitId: string): Promise<Unit> {
    const response = await HttpService.get<Unit>(`/units/${courseId}/${unitId}`);
    return response.data.data;
  }

  public static async getChapter(
    courseId: string,
    chapterId: string,
    type: ChapterItemType,
  ): Promise<Exercise | Unit> {
    if (type === 'Unit') {
      return CourseService.getUnit(courseId, chapterId);
    }
    if (type === 'Exercise') {
      return CourseService.getUserExercise(courseId, chapterId);
    }
    throw new Error(`Unknown type: ${type}`);
  }

  public static async toggleChapterComplete(
    courseId: string,
    chapterId: string,
    chapterType: ChapterType,
  ): Promise<UserCourseStatus> {
    const response = await HttpService.patch(
      `/users/me/courses/${courseId}/status/toggle-chapter-complete`,
      {
        chapterId,
        chapterType,
      },
    );
    return response.data.data as UserCourseStatus;
  }

  public static async updateTimerRemaining(
    courseId: string,
    timerRemaining: number,
  ): Promise<{ timerRemaining: number }> {
    const response = await HttpService.patch(
      `/users/me/courses/${courseId}/status/timer-remaining`,
      { timerRemaining },
    );
    return response.data.data as { timerRemaining: number };
  }

  public static async submitTest(courseId: string): Promise<UserCourseStatus> {
    const response = await HttpService.post(`/users/me/courses/${courseId}/test-results`);
    return response.data.data as UserCourseStatus;
  }

  public static async getTestResults(courseId: string): Promise<TestResult[]> {
    const response = await HttpService.get(`/users/me/courses/${courseId}/test-results`);
    return response.data.data as TestResult[];
  }

  public static async restartTest(courseId: string): Promise<UserCourseStatus> {
    const response = await HttpService.get(`/users/me/courses/${courseId}/test-renew`);
    return response.data.data as UserCourseStatus;
  }

  public static async getChapterNote(reference: string): Promise<ChapterNote> {
    const response = await HttpService.get(`/users/me/chapter-notes/${reference}`);
    return response.data.data as ChapterNote;
  }

  public static async upsertChapterNote(chapterNote: ChapterNote): Promise<ChapterNote> {
    const response = await HttpService.put(
      `/users/me/chapter-notes/${chapterNote.reference}`,
      chapterNote,
    );
    return response.data.data as ChapterNote;
  }

  public static async getFlashcardDecksAndStats(courseId: string): Promise<FlashcardDecksAndStats> {
    const response = await HttpService.get(`/users/me/flashcard-decks/${courseId}`);
    return response.data.data as FlashcardDecksAndStats;
  }

  public static async getFlashcardAndDeckStats(
    courseId: string,
    deckId: string,
  ): Promise<FlashcardAndStats> {
    const response = await HttpService.get(`/users/me/flashcard/${courseId}/${deckId}`);
    return response.data.data as FlashcardAndStats;
  }

  public static async rateFlashcard(
    courseId: string,
    deckId: string,
    flashcardId: string,
    rating: number,
  ): Promise<FlashcardAndStats> {
    const response = await HttpService.patch(`/users/me/flashcard/${courseId}/${deckId}`, {
      flashcardId,
      rating,
    });
    return response.data.data as FlashcardAndStats;
  }
}
